import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import config from '../config/environment';
import {
  ArrowDownTrayIcon,
  CommandLineIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ShareIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const DOWNLOAD_BASE = `${config.apiUrl}/api/v1/downloads`;
const CLI_BASE = `${DOWNLOAD_BASE}/cli`;
const MANUAL_PDF_URL = `${process.env.PUBLIC_URL || ''}/user_manual_governate_cli.pdf`;

// Brand logos (no brand-icon lib in the project, so inline). Apple is a single
// monochrome path; Tux is composed from simple shapes so it renders reliably.
const AppleLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);
// Tux, the Linux mascot — colorful, hand-built to resemble the classic penguin.
const LinuxLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
    {/* feet (behind body) */}
    <path d="M10.5 26.5c-1.9 1.2-3.2 2-2.7 2.9.5.8 3.8.9 5.6.4.8-.2 1.1-1.2.7-2.1z" fill="#f7a90b" stroke="#c47f00" strokeWidth=".4" />
    <path d="M21.5 26.5c1.9 1.2 3.2 2 2.7 2.9-.5.8-3.8.9-5.6.4-.8-.2-1.1-1.2-.7-2.1z" fill="#f7a90b" stroke="#c47f00" strokeWidth=".4" />
    {/* black body */}
    <path d="M16 2.5c-4.8 0-7.8 3.4-7.8 8.6 0 2.7-.4 4.6-1.9 7.4-1.4 2.6-2.2 4.6-1.1 5.9.9 1 2.6.6 3.7.1.5 1.6 1.5 2.9 2.9 3.4 1.2.4 2.7.5 4.2.5s3-.1 4.2-.5c1.4-.5 2.4-1.8 2.9-3.4 1.1.5 2.8.9 3.7-.1 1.1-1.3.3-3.3-1.1-5.9-1.5-2.8-1.9-4.7-1.9-7.4 0-5.2-3-8.6-7.8-8.6z" fill="#1a1a1a" />
    {/* white belly */}
    <path d="M16 12.5c-3.6 0-5.7 3.2-5.7 8.3 0 4.3 2.1 7.2 5.7 7.2s5.7-2.9 5.7-7.2c0-5.1-2.1-8.3-5.7-8.3z" fill="#fff" />
    {/* white eye patches */}
    <ellipse cx="13.1" cy="9.6" rx="2" ry="2.6" fill="#fff" />
    <ellipse cx="18.9" cy="9.6" rx="2" ry="2.6" fill="#fff" />
    {/* pupils */}
    <circle cx="13.7" cy="10.2" r="1" fill="#1a1a1a" />
    <circle cx="18.3" cy="10.2" r="1" fill="#1a1a1a" />
    {/* beak */}
    <path d="M13.9 11.1c-.5 0-.8.6-.4 1l1.9 1.8c.3.3.9.3 1.2 0l1.9-1.8c.4-.4.1-1-.4-1z" fill="#f7a90b" />
    <path d="M14.6 13.2l1.4 1.3 1.4-1.3c-.9.3-1.9.3-2.8 0z" fill="#e08e00" />
  </svg>
);
// Windows logo — four perspective panes.
const WindowsLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M3 5.5 10.2 4.5v6.7H3zM11.3 4.35 21 3v8.2h-9.7zM3 12.3h7.2v6.7L3 18zM11.3 12.3H21V20.5l-9.7-1.3z" />
  </svg>
);

const Downloads: React.FC = () => {
  const { t } = useTranslation('downloads');
  usePageTitle(t('title'));

  const primaryButtonClass = 'bg-black text-white border border-black dark:border-white hover:bg-gray-800 dark:hover:bg-gray-900';
  const cardBorderClass = 'border-2 border-green-500';
  const bgClass = 'bg-gray-50 text-gray-900 dark:bg-black dark:text-white';
  const mutedClass = 'text-gray-600 dark:text-gray-400';
  const subtleCardClass = 'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-white/5 transition-colors';

  const windowsX64Url = `${DOWNLOAD_BASE}/windows/amd64`;
  const macArm64Url = `${CLI_BASE}/darwin/arm64`;
  // One-line install (auto-detects OS + arch) — the primary way to get the CLI.
  const installCmd = `curl -fsSL ${CLI_BASE}/install | sh`;

  // Linux CLI ships amd64 + arm64; the user picks the arch.
  const [linuxArch, setLinuxArch] = useState<'amd64' | 'arm64'>('amd64');
  const [linuxArchOpen, setLinuxArchOpen] = useState(false);
  const linuxUrl = `${CLI_BASE}/linux/${linuxArch}`;

  // The Governate MCP server ships inside the CLI package. Claude Code registers
  // it with one command; other MCP clients use the JSON below.
  const mcpCmd = 'claude mcp add governate -- governate-mcp';
  const mcpConfig = `{
  "mcpServers": {
    "governate": { "command": "governate-mcp" }
  }
}`;

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyText = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className={`min-h-full ${bgClass} transition-colors`}>
      <div className="max-w-5xl mx-auto px-4 py-10 md:py-14">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{t('heading')}</h1>
          <p className={`text-base ${mutedClass}`}>{t('subheading')}</p>
        </div>

        {/* Windows */}
        <section className="mb-12">
          <div className={`max-w-md mx-auto rounded-2xl ${cardBorderClass} p-8 bg-white dark:bg-white/5 transition-colors text-center`}>
            <WindowsLogo className="w-11 h-11 mx-auto mb-3 text-gray-800 dark:text-gray-100" />
            <h3 className="font-bold text-xl">Windows</h3>
            <p className={`text-sm ${mutedClass} mb-5`}>For PC computers (x64 / AMD64)</p>
            <a
              href={windowsX64Url}
              download
              className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors ${primaryButtonClass}`}
            >
              Download for Windows <WindowsLogo className="w-4 h-4" />
            </a>
            <p className={`text-xs ${mutedClass} mt-4`}>Requires Windows 10 or later</p>
          </div>
        </section>

        {/* Command line — CLI + MCP side by side */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-center mb-6">Command line</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* CLI */}
            <div className={`rounded-2xl ${cardBorderClass} p-8 bg-white dark:bg-white/5 transition-colors flex flex-col text-center`}>
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800 mb-3">
                <CommandLineIcon className="w-7 h-7 text-gray-800 dark:text-gray-100" />
              </div>
              <h3 className="font-bold text-xl">CLI</h3>
              <p className={`text-sm ${mutedClass} mb-4`}>Protect and manage files from your terminal</p>

              {/* Install (macOS & Linux) — one-line, copyable */}
              <div className="flex items-center gap-2 mb-4">
                <code className="flex-1 min-w-0 text-xs sm:text-sm bg-black text-white rounded-lg px-3 py-2.5 overflow-x-auto whitespace-nowrap text-left">
                  {installCmd}
                </code>
                <button
                  onClick={() => copyText('cli', installCmd)}
                  className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="Copy install command"
                  aria-label="Copy install command"
                >
                  {copiedKey === 'cli' ? <CheckIcon className="w-5 h-5 text-green-600" /> : <ClipboardDocumentIcon className="w-5 h-5" />}
                </button>
              </div>

              {/* Or download a package directly */}
              <p className={`text-xs ${mutedClass} text-left mb-2`}>
                Or download a package — extract it, then run <code className="text-[11px]">chmod +x install.sh &amp;&amp; ./install.sh</code> inside:
              </p>

              {/* Mac (Apple Silicon) */}
              <a
                href={macArm64Url}
                download
                className={`flex items-center justify-between gap-2 px-4 py-3 rounded-lg mb-3 transition-colors ${primaryButtonClass}`}
              >
                <span className="flex items-center gap-2"><AppleLogo className="w-4 h-4" /> Mac</span>
                <span className="text-sm opacity-70">Apple Silicon</span>
              </a>

              {/* Linux (arch selectable) */}
              <div className="relative mb-3">
                <div className={`flex items-center justify-between gap-2 pl-4 pr-2 rounded-lg transition-colors ${primaryButtonClass}`}>
                  <a href={linuxUrl} download className="flex items-center gap-2 flex-1 py-3">
                    <LinuxLogo className="w-5 h-5" /> Linux
                  </a>
                  <button
                    type="button"
                    onClick={() => setLinuxArchOpen((o) => !o)}
                    className="flex items-center gap-1 text-sm opacity-80 px-2 py-1.5 rounded hover:bg-white/10"
                    aria-haspopup="listbox"
                    aria-expanded={linuxArchOpen}
                  >
                    {linuxArch} <ChevronDownIcon className={`w-4 h-4 transition-transform ${linuxArchOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {linuxArchOpen && (
                  <div className="absolute right-0 mt-1 w-32 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden z-10">
                    {(['amd64', 'arm64'] as const).map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => { setLinuxArch(a); setLinuxArchOpen(false); }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <p className={`text-xs ${mutedClass} mt-auto`}>Mac Apple Silicon and Linux amd64 / arm64</p>
            </div>

            {/* MCP */}
            <div className={`rounded-2xl ${cardBorderClass} p-8 bg-white dark:bg-white/5 transition-colors flex flex-col text-center`}>
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800 mb-3">
                <ShareIcon className="w-7 h-7 text-gray-800 dark:text-gray-100" />
              </div>
              <h3 className="font-bold text-xl">MCP Server</h3>
              <p className={`text-sm ${mutedClass} mb-4`}>Connect Governate to your AI agents and tools</p>
              <span className="inline-flex items-center gap-1.5 self-center text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 mb-5">
                <CommandLineIcon className="w-3.5 h-3.5" /> Included with the Governate CLI
              </span>

              <p className={`text-sm ${mutedClass} text-left mb-2`}>
                Start the Governate CLI, then register it with your agent (e.g. Claude Code):
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 text-xs sm:text-sm bg-black text-white rounded-lg px-3 py-2.5 overflow-x-auto whitespace-nowrap text-left">
                  {mcpCmd}
                </code>
                <button
                  onClick={() => copyText('mcp', mcpCmd)}
                  className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="Copy command"
                  aria-label="Copy MCP command"
                >
                  {copiedKey === 'mcp' ? <CheckIcon className="w-5 h-5 text-green-600" /> : <ClipboardDocumentIcon className="w-5 h-5" />}
                </button>
              </div>
              <p className={`text-xs ${mutedClass} text-left mt-3`}>
                Runs your locally installed, activated Governate CLI. macOS and Linux.
              </p>

              <details className="text-left mt-4">
                <summary className={`text-xs ${mutedClass} cursor-pointer select-none`}>Other MCP clients — config</summary>
                <div className="relative mt-2">
                  <pre className="text-xs bg-gray-100 dark:bg-gray-900 rounded-lg p-3 overflow-x-auto"><code>{mcpConfig}</code></pre>
                  <button
                    onClick={() => copyText('mcpjson', mcpConfig)}
                    className="absolute top-2 right-2 inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-black/40 hover:bg-gray-100 dark:hover:bg-gray-800"
                    title="Copy config"
                    aria-label="Copy MCP config"
                  >
                    {copiedKey === 'mcpjson' ? <CheckIcon className="w-4 h-4 text-green-600" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
                  </button>
                </div>
              </details>
            </div>
          </div>
        </section>

        {/* Resources & Documentation */}
        <section className="mb-4">
          <h2 className="text-lg font-semibold text-center mb-4">Resources &amp; Documentation</h2>
          <a
            href={MANUAL_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`${subtleCardClass} p-5 flex items-center gap-4 hover:border-green-500`}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40 shrink-0">
              <DocumentTextIcon className="w-6 h-6 text-green-700 dark:text-green-300" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold">Governate CLI — User Guide (PDF)</h3>
              <p className={`text-sm ${mutedClass}`}>Install, activate, encrypt, share & manage files from your terminal.</p>
            </div>
            <ArrowDownTrayIcon className="w-5 h-5 ml-auto shrink-0 text-gray-400" />
          </a>
        </section>
      </div>
    </div>
  );
};

export default Downloads;
